<script setup lang="ts">
import type { AxiosError } from 'axios';
import { useUserStore } from '~/store/user';
import * as z from 'zod';
import type { FormSubmitEvent } from '@nuxt/ui';
import { LOGIN_REDIRECT_QUERY_KEY, resolveSafeRedirectTarget } from '~/utils/auth';
import type { MwBffAccountSelectionResponse } from '~/types/mw-bff';

definePageMeta({
  layout: 'auth'
})

useSeoMeta({
  title: 'Login'
})

// Identifier-first flow: collect the email first (with social SSO above it),
// then the password on a second, compact step. Multi-account and 2FA follow.
type Step = 'email' | 'password' | 'account-selection' | 'two-factor'

const step = ref<Step>('email')
const isLoading = ref(false)
const loginError = ref('')
const passwordVisibility = ref(false)

const userStore = useUserStore()
const toast = useToast()
const { t: $t } = useI18n()
const { termsUrl } = useLegalLinks()
const localePath = useLocalePath()
const route = useRoute()

// Carry the post-login destination (e.g. checkout) through the social round-trip.
const socialRedirect = computed(() =>
  resolveSafeRedirectTarget(route.query[LOGIN_REDIRECT_QUERY_KEY], '/')
)

// "Sign up" carries the same destination on. It was a bare `/register`, so a
// newcomer the sign-in wall had stopped — on the way to a page, or in from an
// affiliate's `/?ref=…` link — created an account and lost both.
const registerLink = computed(() => ({
  path: localePath('/register'),
  query: socialRedirect.value !== '/' ? { [LOGIN_REDIRECT_QUERY_KEY]: socialRedirect.value } : {},
}))

const emailSchema = z.object({
  email: z.string().email()
})
type EmailSchema = z.infer<typeof emailSchema>

const passwordSchema = z.object({
  password: z.string().min(1)
})
type PasswordSchema = z.infer<typeof passwordSchema>

const credentials = reactive({
  email: '',
  password: '',
  remember: false
})

// Account selection step state
const accountSelectionData = ref<MwBffAccountSelectionResponse | null>(null)
const selectedClientId = ref<number | null>(null)
const accountSelectionOrigin = ref<'credentials' | 'two-factor'>('credentials')

// 2FA step state — the code entry, resend budget and verification call
// belong to <AuthTwoFactorStep>, shared with /register.
const challengeId = ref<string | null>(null)

function togglePasswordVisibility() {
  passwordVisibility.value = !passwordVisibility.value
}

// Focus the password field when entering the password step.
watch(step, (value) => {
  if (value === 'password') {
    nextTick(() => {
      document.querySelector<HTMLInputElement>('input[name="login-password"]')?.focus()
    })
  }
})

async function completeLogin(result: any) {
  const redirectTarget = resolveSafeRedirectTarget(route.query[LOGIN_REDIRECT_QUERY_KEY], '/')
  if (result?.client !== undefined || result?.sessionId) {
    toast.add({ title: $t('page.login.success'), color: 'success' })
    await userStore.setUserDefaultLanguage()
    await navigateTo(localePath(redirectTarget))
  }
}

function handleError(e: unknown, clientErrorMessageKey = 'page.login.invalid') {
  const error = e as AxiosError
  const status = error.response?.status
  if (status && status >= 400 && status < 500) {
    loginError.value = $t(clientErrorMessageKey)
    toast.add({ title: $t(clientErrorMessageKey), color: 'error' })
  } else {
    loginError.value = $t('general.error.description')
    toast.add({
      title: $t('general.error.title'),
      description: $t('general.error.description'),
      color: 'error'
    })
  }
}

// Step 1 → 2: identifier-first, no server round-trip (avoids account enumeration).
function onSubmitEmail(_event: FormSubmitEvent<EmailSchema>) {
  loginError.value = ''
  step.value = 'password'
}

function backToEmail() {
  loginError.value = ''
  credentials.password = ''
  passwordVisibility.value = false
  step.value = 'email'
}

// Password reset via WHMCS API (generic response — no account enumeration).
const sendingReset = ref(false)
const resetSent = ref(false)

async function onForgotPassword() {
  const email = credentials.email?.trim()
  if (!email || sendingReset.value) return
  sendingReset.value = true
  try {
    const { bffFetch } = useMwBff()
    await bffFetch('/whmcs/password/reset', { method: 'POST', body: { email } })
  } catch {
    // Backend always returns success; show the same confirmation regardless.
  } finally {
    sendingReset.value = false
    resetSent.value = true
    toast.add({
      title: $t('page.login.resetSentTitle'),
      description: $t('page.login.resetSentBody', { email }),
      color: 'success',
    })
  }
}

async function onSubmitPassword(_event: FormSubmitEvent<PasswordSchema>) {
  loginError.value = ''
  isLoading.value = true

  try {
    const result = await userStore.login(credentials.email, credentials.password, credentials.remember)

    if (!result) {
      loginError.value = $t('page.login.invalid')
      toast.add({ title: $t('page.login.invalid'), color: 'error' })
      return
    }

    if ('result' in result && result.result === 'account_selection_required') {
      accountSelectionData.value = result as MwBffAccountSelectionResponse
      selectedClientId.value = (result as MwBffAccountSelectionResponse).accounts[0]?.clientId ?? null
      accountSelectionOrigin.value = 'credentials'
      step.value = 'account-selection'
      return
    }

    if ('result' in result && result.result === 'two_factor_required') {
      challengeId.value = (result as any).challengeId
      step.value = 'two-factor'
      return
    }

    await completeLogin(result)
  } catch (e) {
    handleError(e)
  } finally {
    isLoading.value = false
  }
}

async function onSubmitAccountSelection(clientId: number) {
  // `selectedClientId` is still the page's, because <AuthTwoFactorStep
  // :client-id> reads it — the component below owns the CHOICE, not the record
  // of what was chosen.
  selectedClientId.value = clientId
  loginError.value = ''
  isLoading.value = true

  const { deviceId } = useDeviceId()

  try {
    // When account selection follows 2FA, reuse the verified challenge instead
    // of calling login() again (which would re-trigger 2FA)
    const result = accountSelectionOrigin.value === 'two-factor' && challengeId.value
      ? await userStore.verifyTwoFactor(
          challengeId.value,
          undefined,
          deviceId,
          credentials.remember,
          selectedClientId.value
        )
      : await userStore.login(
          credentials.email,
          credentials.password,
          credentials.remember,
          selectedClientId.value
        )

    if (!result) {
      loginError.value = $t('page.login.invalid')
      toast.add({ title: $t('page.login.invalid'), color: 'error' })
      return
    }

    if ('result' in result && result.result === 'two_factor_required') {
      challengeId.value = (result as any).challengeId
      step.value = 'two-factor'
      return
    }

    await completeLogin(result)
  } catch (e) {
    handleError(e)
  } finally {
    isLoading.value = false
  }
}

// <AuthTwoFactorStep> owns the code entry and the verify call; the page only
// routes what comes back.
function onTwoFactorAccountSelection(result: MwBffAccountSelectionResponse) {
  accountSelectionData.value = result
  selectedClientId.value = result.accounts[0]?.clientId ?? null
  accountSelectionOrigin.value = 'two-factor'
  step.value = 'account-selection'
}

async function onTwoFactorVerified(result: any) {
  await completeLogin(result)
}

function onTwoFactorError(message: string) {
  loginError.value = message
  toast.add({ title: message, color: 'error' })
}

// Full reset back to the first step (used by 2FA / account-selection "back").
function resetToEmail() {
  loginError.value = ''
  credentials.password = ''
  challengeId.value = null
  accountSelectionData.value = null
  selectedClientId.value = null
  accountSelectionOrigin.value = 'credentials'
  step.value = 'email'
}

// Pre-fill the email (e.g. arriving from the "account already exists" prompt on
// the register page) and jump straight to the password step.
onMounted(() => {
  const prefill = route.query.email
  if (typeof prefill === 'string' && prefill.includes('@')) {
    credentials.email = prefill
    step.value = 'password'
  }
})

const headerIcon = computed(() => {
  switch (step.value) {
    case 'two-factor': return 'i-lucide-shield-check'
    case 'account-selection': return 'i-lucide-users'
    case 'password': return 'i-lucide-lock-keyhole'
    default: return 'i-lucide-user-round'
  }
})

const headerTitle = computed(() => {
  switch (step.value) {
    case 'two-factor': return $t('page.login.twoFactor.title')
    case 'account-selection': return $t('page.login.accountSelection.title')
    case 'password': return $t('page.login.passwordTitle')
    default: return $t('page.login.formTitle')
  }
})
</script>

<template>
  <div class="w-full">

    <!-- Header -->
    <div class="text-center mb-6">
      <div class="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-100 dark:bg-primary-900/30 mb-3">
        <UIcon :name="headerIcon" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
      </div>
      <h1 class="text-2xl text-gray-900 dark:text-white font-bold">
        {{ headerTitle }}
      </h1>

      <p v-if="step === 'email'" class="text-sm text-gray-600 dark:text-gray-300 mt-1">
        {{ $t('page.login.notRegistered') }}
        <NuxtLink :to="registerLink" class="text-primary-600 dark:text-primary-400 font-semibold hover:underline">
          {{ $t('page.login.signUp') }}
        </NuxtLink>
      </p>

      <!-- Identifier chip with "change" on the password step -->
      <button
        v-else-if="step === 'password'"
        type="button"
        class="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
        @click="backToEmail"
      >
        <UIcon name="i-lucide-mail" class="w-4 h-4 shrink-0" />
        <span class="truncate max-w-[14rem]">{{ credentials.email }}</span>
        <span class="text-primary-600 dark:text-primary-400 font-medium">{{ $t('page.login.changeEmail') }}</span>
      </button>

      <p v-else-if="step === 'account-selection'" class="text-sm text-gray-600 dark:text-gray-300 mt-1">
        {{ $t('page.login.accountSelection.description') }}
      </p>
      <p v-else-if="step === 'two-factor'" class="text-sm text-gray-600 dark:text-gray-300 mt-1">
        {{ $t('page.login.twoFactor.description', { email: credentials.email }) }}
      </p>
    </div>

    <!-- Error alert -->
    <UAlert
      v-if="loginError"
      color="error"
      icon="i-heroicons-exclamation-triangle"
      class="mb-5"
      :title="loginError"
    />

    <!-- Step: Email (identifier-first) + social -->
    <div v-if="step === 'email'" class="space-y-5">
      <AuthSocialLoginButtons :redirect="socialRedirect" />

      <div class="flex items-center gap-3">
        <USeparator class="flex-1" />
        <span class="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {{ $t('page.login.orContinueWith') }}
        </span>
        <USeparator class="flex-1" />
      </div>

      <UForm :schema="emailSchema" :state="credentials" class="space-y-4" @submit="onSubmitEmail">
        <UFormField :label="$t('page.login.email')" name="email">
          <UInput
            v-model="credentials.email"
            type="email"
            size="xl"
            :placeholder="$t('page.login.loginInputPlaceholder')"
            icon="i-heroicons-envelope"
            autocomplete="email"
            class="w-full"
          />
        </UFormField>

        <UButton
          type="submit"
          size="xl"
          block
          trailing-icon="i-heroicons-arrow-right-20-solid"
          class="shadow-lg hover:shadow-xl transition-all"
        >
          {{ $t('page.login.continue') }}
        </UButton>
      </UForm>
    </div>

    <!-- Step: Password -->
    <UForm v-else-if="step === 'password'" :schema="passwordSchema" :state="credentials" class="space-y-4" @submit="onSubmitPassword">
      <UFormField :label="$t('page.login.password')" name="password">
        <UInput
          v-model="credentials.password"
          name="login-password"
          :type="passwordVisibility ? 'text' : 'password'"
          size="xl"
          :placeholder="$t('page.login.passwordInputPlaceholder')"
          icon="i-heroicons-lock-closed"
          autocomplete="current-password"
          class="w-full"
        >
          <template #trailing>
            <UButton
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              variant="link"
              :icon="passwordVisibility ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              :aria-label="passwordVisibility ? 'Hide password' : 'Show password'"
              :aria-pressed="passwordVisibility"
              @click="togglePasswordVisibility"
            />
          </template>
        </UInput>
      </UFormField>

      <div class="flex items-center justify-between">
        <UCheckbox
          v-model="credentials.remember"
          name="remember"
          :label="$t('page.login.rememberMe')"
        />
        <UButton
          variant="link"
          size="sm"
          :loading="sendingReset"
          class="text-primary-600 dark:text-primary-400 font-medium px-0"
          @click="onForgotPassword"
        >
          {{ resetSent ? $t('page.login.resetSentLink') : $t('page.login.forgotPassword') }}
        </UButton>
      </div>

      <p v-if="resetSent" class="text-xs text-center text-gray-500 dark:text-gray-400 -mt-1">
        {{ $t('page.login.resetSentBody', { email: credentials.email }) }}
      </p>

      <UButton
        type="submit"
        size="xl"
        block
        :loading="isLoading"
        trailing-icon="i-heroicons-arrow-right-20-solid"
        class="shadow-lg hover:shadow-xl transition-all"
      >
        {{ $t('page.login.continue') }}
      </UButton>
    </UForm>
    <!-- Step: Account selection.
         Shared with the social callback and the checkout's commit sheet, for
         the same reason AuthTwoFactorStep is: all three surfaces can be handed
         `account_selection_required`, and the two hand-written copies had
         already drifted apart. -->
    <AuthAccountSelectionStep
      v-else-if="step === 'account-selection'"
      :accounts="accountSelectionData?.accounts ?? []"
      :loading="isLoading"
      show-back
      @select="onSubmitAccountSelection"
      @back="resetToEmail"
    />

    <!-- Step: Two-factor -->
    <AuthTwoFactorStep
      v-else-if="step === 'two-factor' && challengeId"
      :email="credentials.email"
      :challenge-id="challengeId"
      :remember-me="credentials.remember"
      :client-id="selectedClientId"
      @verified="onTwoFactorVerified"
      @account-selection="onTwoFactorAccountSelection"
      @error="onTwoFactorError"
      @back="resetToEmail"
    />

    <!-- Terms of Service (email step only) -->
    <div v-if="step === 'email'" class="text-xs text-gray-500 dark:text-gray-400 mt-6 text-center">
      {{ $t('page.login.tosDescription') }}
      <NuxtLink
        v-if="termsUrl"
        :to="termsUrl"
        target="_blank"
        class="text-primary-600 dark:text-primary-400 font-semibold hover:underline"
      >
        {{ $t('page.login.tos') }}
      </NuxtLink>
      <span v-else class="font-semibold">{{ $t('page.login.tos') }}</span>
    </div>

  </div>
</template>

<style scoped>
:deep(.u-form-field) {
  transition: all 0.2s ease;
}

:deep(.u-input) {
  transition: all 0.2s ease;
}

:deep(input),
:deep(button) {
  transition: all 0.2s ease;
}

@media (max-width: 640px) {
  :deep(.u-button) {
    min-height: 44px;
  }

  :deep(.u-input input) {
    min-height: 44px;
  }
}
</style>
